import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, TableBody, TableCell, TableHead,
  TableRow, TextField, Alert, CircularProgress, Chip
} from '@mui/material';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import { formatAmount } from '../../utils/money';
import ScrollTable from '../ScrollTable';

// Grouped without a currency prefix — these tables carry the currency in their heading.
const fmt = (n) => formatAmount(n);

const today = () => new Date().toISOString().slice(0, 10);

const Section = ({ title, accounts, total, note }) => (
  <Box mb={3}>
    <Typography variant="subtitle1" fontWeight={700} gutterBottom>{title}</Typography>
    <ScrollTable containerProps={{ component: Paper, variant: 'outlined', sx: { overflowX: 'auto' } }} size="small">
        <TableHead>
          <TableRow>
            <TableCell>Code</TableCell>
            <TableCell>Account</TableCell>
            <TableCell align="right">Balance</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {accounts.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} sx={{ color: 'text.secondary' }}>Nothing posted</TableCell>
            </TableRow>
          )}
          {accounts.map((a) => (
            <TableRow key={a.accountId}>
              <TableCell>{a.code}</TableCell>
              <TableCell>
                {a.name}
                {a.isContraAccount && (
                  <Chip label="contra" size="small" sx={{ ml: 1 }} variant="outlined" />
                )}
              </TableCell>
              <TableCell align="right">{fmt(a.balance)}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Total {title}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(total)}</TableCell>
          </TableRow>
        </TableBody>
      </ScrollTable>
    {note && (
      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>{note}</Typography>
    )}
  </Box>
);

const BalanceSheet = () => {
  const [asOf, setAsOf] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSheet = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/accounting/reports/balance-sheet?asOf=${asOf}`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load the balance sheet');
    } finally {
      setLoading(false);
    }
  }, [asOf]);

  useEffect(() => { fetchSheet(); }, [fetchSheet]);

  return (
    <Box>
      <Box mb={2}>
        <TextField
          type="date" label="As at" size="small" value={asOf}
          onChange={(e) => setAsOf(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {loading && <CircularProgress size={24} />}

      {data && (
        <>
          {/* The identity is arithmetic: every entry balances, so this can only be off if an
              unbalanced entry reached the ledger. Saying so loudly beats presenting figures
              that quietly do not add up. */}
          {!data.balanced && (
            <Alert severity="error" sx={{ mb: 2 }}>
              This balance sheet does not balance — assets differ from liabilities plus equity
              by {fmt(data.difference)}. An unbalanced journal entry has reached the ledger.
              The books need investigating before this statement is relied on.
            </Alert>
          )}

          <Section title="Assets" accounts={data.assets.accounts} total={data.assets.total} />
          <Section title="Liabilities" accounts={data.liabilities.accounts} total={data.liabilities.total} />

          <Box mb={3}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Equity</Typography>
            <ScrollTable containerProps={{ component: Paper, variant: 'outlined', sx: { overflowX: 'auto' } }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell align="right">Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.equity.accounts.map((a) => (
                    <TableRow key={a.accountId}>
                      <TableCell>{a.code}</TableCell>
                      <TableCell>{a.name}</TableCell>
                      <TableCell align="right">{fmt(a.balance)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={2}>
                      Profit for the period, not yet closed
                    </TableCell>
                    <TableCell align="right">{fmt(data.equity.currentEarnings)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Total Equity</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(data.equity.total)}</TableCell>
                  </TableRow>
                </TableBody>
              </ScrollTable>
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              Earnings since the last year-end close sit here until the year is closed, when
              they move into Retained Earnings.
            </Typography>
          </Box>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography fontWeight={700}>Total Assets</Typography>
              <Typography fontWeight={700}>{fmt(data.assets.total)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography fontWeight={700}>Total Liabilities and Equity</Typography>
              <Typography fontWeight={700}>{fmt(data.totalLiabilitiesAndEquity)}</Typography>
            </Box>
            {data.balanced && (
              <Typography variant="caption" color="success.main" display="block" mt={1}>
                Balanced.
              </Typography>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default BalanceSheet;
